import { useEffect, useState, type ChangeEvent } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import classnames from "classnames";

import {
  Ial2CheckboxLabel,
  Ial2CheckboxSublabel,
  SmsAuthCheckboxLabel,
  SmsAuthCheckboxSublabel,
  KbaCheckboxLabel,
  KbaCheckboxSublabel,
} from "common/proof_requirements/common";
import Icon from "common/core/icon";
import { TextInput } from "common/core/form/text";
import {
  useController,
  type FieldValues,
  type UseControllerProps,
  type UseFormReturn,
} from "common/core/form";
import {
  RadioGroup,
  RadioLabel,
  RadioInput,
  CheckboxGroup,
  CheckboxLabel,
  Checkbox,
  type PresentNode,
} from "common/core/form/option";
import FormRow from "common/form/elements/row";
import {
  AuthTypes,
  OrganizationApiKeyPayer,
  ProofRequirementMfa,
  type ProofRequirementInputType,
} from "graphql_globals";
import { AutomaticFormRow, Label } from "common/core/form/layout";
import RequiredAsterisk from "common/core/form/required-asterisk";
import Link from "common/core/link";
import EditPayment from "common/transactions/form/sub_forms/payment";
import { useOrgRole } from "common/organization/organization_role_context";
import { pushNotification } from "common/core/notification_center/actions";
import BinaryToggle from "common/form/inputs/binary_toggle";
import { FieldErrorMessage } from "common/core/form/error";
import { useId } from "util/html";
import { supportHost } from "constants/support";
import { Paragraph } from "common/core/typography/index";
import { Select } from "common/core/form/select";
import AlertMessage from "common/core/alert_message";

import { FormCard } from "../common";
import Styles from "./common.module.scss";

export const COMMON_MESSAGES = defineMessages({
  NAME_NOT_PROVIDED: {
    id: "6ccb5d6b-edfe-4c29-bafa-dbf145dfe76a",
    defaultMessage: "Name not provided",
  },
  ERROR_MESSAGE: {
    id: "01b9cf7f-016f-4137-bdbb-1cf2c7f7fe23",
    defaultMessage: "Sorry, something went wrong. Please try again.",
  },
  NAME_LABEL: {
    id: "8e9fe8bb-4492-48dd-9550-4d2c9be073df",
    defaultMessage: "EasyLink name",
  },
});

const MESSAGES = defineMessages({
  NAME_REQUIRED_MESSAGE: {
    id: "d59653ce-6a85-4de5-a464-9a8cb78fe39c",
    defaultMessage: "An EasyLink name is required.",
  },
  NAME_HELPER_TEXT: {
    id: "4d1c99d8-01f9-45b1-827f-a3a4a6554d52",
    defaultMessage: "This will only be visible to you and your team.",
  },
  NAME_PLACEHOLDER: {
    id: "f1087e57-9de7-470d-a5b0-cf7dd177ea8f",
    defaultMessage: "ex. Power of Attorney",
  },
  UPDATE_PAYMENT_SUCCESS: {
    id: "cf1b3999-72ba-4c06-af64-be2856ff22d7",
    defaultMessage: "Account payment method added successfully.",
  },
  PROOF_REQUIREMENT_ERROR: {
    id: "59e7459b-3047-43e2-9a84-d9c03b5e7d97",
    defaultMessage: "Please select at least one identity verification option.",
  },
});

export type ProofRequirementFormType = "SMS" | "IAL2" | "KBA";

type NameField = {
  name: string;
};
export function NameInput<FormFields extends NameField>({
  form: untypedFormReturn,
}: {
  form: UseFormReturn<FormFields>;
}) {
  // https://github.com/react-hook-form/react-hook-form/issues/6726
  const form = untypedFormReturn as unknown as UseFormReturn<NameField>;

  const intl = useIntl();

  return (
    <FormCard
      cardTitle={
        <>
          <FormattedMessage
            id="19bac3a4-b9d4-4aff-af10-bac014cc9668"
            defaultMessage="Enter a name for your EasyLink"
          />
          <RequiredAsterisk />
        </>
      }
      cardDescription={
        <FormattedMessage
          id="d9641c21-b890-49b7-b4b6-32537f8fdd4d"
          defaultMessage="This will only be visible to you and your team."
        />
      }
    >
      <AutomaticFormRow
        form={form}
        name="name"
        aria-label={intl.formatMessage(COMMON_MESSAGES.NAME_LABEL)}
        placeholder={intl.formatMessage(MESSAGES.NAME_PLACEHOLDER)}
        registerOptions={{ required: intl.formatMessage(MESSAGES.NAME_REQUIRED_MESSAGE) }}
        dynamicHeightError
        highlightLabelOnError
        as={TextInput}
      />
    </FormCard>
  );
}

export function EditableTitle<FormValues extends FieldValues>(
  props: UseControllerProps<FormValues> & { "aria-label"?: string },
) {
  const intl = useIntl();
  const { field } = useController(props);
  const [fieldValue, setFieldValue] = useState<string | undefined>(field.value);
  const [editing, setEditing] = useState(false);

  const revertToOriginal = () => {
    setFieldValue(field.value || "");
    setEditing(false);
  };

  const propagateValueOrRevert = () => {
    // revert value to original if input is empty
    if (fieldValue?.length) {
      field.onChange({ target: { value: fieldValue } } as unknown as ChangeEvent);
      setEditing(false);
    } else {
      revertToOriginal();
    }
  };

  return editing ? (
    <TextInput
      {...field}
      className={Styles.editableTitleInput}
      aria-invalid={!field.value?.length}
      aria-label={props["aria-label"]}
      value={fieldValue}
      onBlur={propagateValueOrRevert}
      onKeyDown={(event) => {
        switch (event.key) {
          case "Enter":
            return propagateValueOrRevert();
          case "Escape":
          case "Esc":
            return revertToOriginal();
        }
      }}
      onChange={(event) => setFieldValue(event.target.value)}
      autoFocus
    />
  ) : (
    <button
      type="button"
      className={`${Styles.title} ${Styles.editableTitle}`}
      onClick={() => setEditing(true)}
    >
      <span>
        {field.value || intl.formatMessage(COMMON_MESSAGES.NAME_NOT_PROVIDED)}
        <Icon name="pencil-line" className={Styles.editableTitleIcon} />
      </span>
    </button>
  );
}

type PayerField = { payer: OrganizationApiKeyPayer };
export function PayerRadioGroup<FormFields extends PayerField>({
  form: untypedFormReturn,
  canOrgPay,
}: {
  form: UseFormReturn<FormFields>;
  canOrgPay: boolean;
}) {
  const intl = useIntl();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const orgRole = useOrgRole();

  // https://github.com/react-hook-form/react-hook-form/issues/6726
  const form = untypedFormReturn as unknown as UseFormReturn<PayerField>;
  const { watch, register } = form;
  const payer = watch("payer");

  const radioGroupIdLabel = useId();
  const radioGroupIdDescribedBy = useId();

  return (
    <FormCard
      cardTitle={
        <span id={radioGroupIdLabel}>
          <FormattedMessage
            id="0803975c-ad09-4403-bda2-23bea69f78e5"
            defaultMessage="Who will be paying for the transaction?"
          />
        </span>
      }
      cardDescription={
        <span id={radioGroupIdDescribedBy}>
          <FormattedMessage
            id="c8dd4782-5285-4c8a-83c2-b6d83de60910"
            defaultMessage="This will override your default account payment settings for any transactions using this EasyLink."
          />
        </span>
      }
    >
      {showPaymentModal && (
        <EditPayment
          collectOrgPaymentInfo
          orgRole={orgRole}
          onCancel={() => setShowPaymentModal(false)}
          onComplete={() => {
            pushNotification({
              position: "topCenter",
              message: intl.formatMessage(MESSAGES.UPDATE_PAYMENT_SUCCESS),
            });
            setShowPaymentModal(false);
          }}
        />
      )}
      <FormRow>
        <RadioGroup aria-labelledby={radioGroupIdLabel} aria-describedby={radioGroupIdDescribedBy}>
          <RadioLabel
            label={
              <FormattedMessage
                id="53179f76-c329-4516-a68a-10635554fe1f"
                defaultMessage="The signer"
              />
            }
            radio={<RadioInput value={OrganizationApiKeyPayer.CUSTOMER} {...register("payer")} />}
          />
          <RadioLabel
            label={
              <FormattedMessage
                id="6ef5323c-11af-4d02-8806-05ca45a3729c"
                defaultMessage="My organization"
              />
            }
            radio={
              <RadioInput
                disabled={!canOrgPay}
                value={OrganizationApiKeyPayer.ORGANIZATION}
                {...register("payer")}
              />
            }
          />
          {!canOrgPay && (
            <div className={Styles.organizationPayerWarning}>
              <FormattedMessage
                id="9dddb268-a3ec-42ac-aa4f-5045e22d53e8"
                defaultMessage={
                  "<paymentLink>Add a payment method</paymentLink> to your account to select this option."
                }
                values={{
                  paymentLink: (text) => (
                    <Link
                      className={Styles.addPaymentLink}
                      underlined
                      onClick={() => setShowPaymentModal(true)}
                    >
                      {text}
                    </Link>
                  ),
                }}
              />
            </div>
          )}
          {payer === OrganizationApiKeyPayer.ORGANIZATION && (
            <div className={Styles.organizationPayerWarning}>
              <Icon className={Styles.organizationPayerWarningIcon} name="warning" />
              <FormattedMessage
                id="64cc17fc-7542-41c5-b3e2-85efd8ae4b74"
                defaultMessage="Signers can potentially share EasyLinks outside of your intended audience."
              />
            </div>
          )}
        </RadioGroup>
      </FormRow>
    </FormCard>
  );
}

type ProofField = { proofRequirement?: ProofRequirementFormType[] };
export function ProofRequirement<FormFields extends ProofField>({
  disabled,
  form: untypedFormReturn,
  defaultAuthenticationRequirement,
  disabledMessage,
  proofSectionSubcopy,
}: {
  disabled?: boolean;
  form: UseFormReturn<FormFields>;
  defaultAuthenticationRequirement: AuthTypes;
  disabledMessage: PresentNode;
  proofSectionSubcopy: PresentNode;
}) {
  const intl = useIntl();
  const proofToggleId = useId();

  // https://github.com/react-hook-form/react-hook-form/issues/6726
  const form = untypedFormReturn as unknown as UseFormReturn<ProofField>;
  const { register, formState, unregister, resetField, setValue } = form;
  const name = "proofRequirement";
  const error = formState.errors[name];
  const proofRequirement = form.watch("proofRequirement");
  const defaultAuthRequirement: ProofRequirementFormType[] =
    defaultAuthenticationRequirement === AuthTypes.SMS ? ["SMS"] : [];

  useEffect(() => {
    if (!formState.defaultValues?.proofRequirement) {
      resetField(name, { defaultValue: defaultAuthRequirement });
    }
  }, []);

  useEffect(() => {
    if (disabled) {
      unregister(name);
    }
  }, [disabled]);

  function handleShowProofOptions() {
    // proofRequirement can be [] with form errors
    // setValue to trigger the isDirty on edit from
    // https://github.com/react-hook-form/react-hook-form/issues/406
    if (proofRequirement === undefined) {
      register(name);
      resetField(name);
      setValue(name, defaultAuthRequirement, { shouldDirty: true });
    } else {
      setValue(name, [], { shouldDirty: true });
      unregister(name);
    }
  }
  const formGroupLabelId = useId();

  return (
    <FormCard
      disabled={disabled}
      cardTitle={
        <div className={classnames(Styles.proofRequirementTitle, disabled && Styles.disabled)}>
          <span id={proofToggleId}>
            <FormattedMessage
              id="33712359-b04a-4fe3-86dc-549f7d839173"
              defaultMessage="Add identity verification with Proof"
            />
          </span>
          {!disabled && <RequiredAsterisk />}
          <BinaryToggle
            className={Styles.proofRequirementToggle}
            aria-labelledby={proofToggleId}
            onChange={handleShowProofOptions}
            value={!disabled && Boolean(proofRequirement)}
            disabled={disabled}
          />
        </div>
      }
      cardDescription={disabled && disabledMessage}
      tipWellHeader={
        <FormattedMessage
          id="7c7bd8a7-818c-4153-90a0-3ee6f29eb169"
          defaultMessage="What is Proof?"
        />
      }
      tipWellContent={
        <FormattedMessage
          id="923b4978-f8e7-48d0-952e-6cb96726975c"
          defaultMessage="Proof provides a heightened level of assurance to validate a signer's identity, prior to them electronically signing a document. <link>Learn more</link>"
          values={{
            link: (text) => (
              <Link href={`${supportHost}/hc/en-us/articles/8706936036887`} underlined>
                {text}
              </Link>
            ),
          }}
        />
      }
    >
      <>
        {proofRequirement !== undefined && (
          <>
            <Paragraph id={formGroupLabelId}>{proofSectionSubcopy}</Paragraph>
            <FormRow>
              <CheckboxGroup
                groupError={error && <FieldErrorMessage inputName={name} message={error.message} />}
                aria-labelledby={formGroupLabelId}
              >
                <CheckboxLabel
                  label={<Ial2CheckboxLabel />}
                  subLabel={<Ial2CheckboxSublabel />}
                  checkbox={
                    <Checkbox
                      aria-invalid={Boolean(error)}
                      value="IAL2"
                      data-automation-id="ial2-proof-checkbox"
                      {...register(name, {
                        required: intl.formatMessage(MESSAGES.PROOF_REQUIREMENT_ERROR),
                      })}
                    />
                  }
                />
                <CheckboxLabel
                  disabled={proofRequirement.includes("IAL2")}
                  label={<SmsAuthCheckboxLabel />}
                  subLabel={<SmsAuthCheckboxSublabel />}
                  checkbox={
                    <Checkbox
                      aria-invalid={Boolean(error)}
                      value="SMS"
                      disabled={proofRequirement.includes("IAL2")}
                      data-automation-id="sms-proof-checkbox"
                      {...register(name, {
                        required: intl.formatMessage(MESSAGES.PROOF_REQUIREMENT_ERROR),
                      })}
                    />
                  }
                />
                <CheckboxLabel
                  label={<KbaCheckboxLabel />}
                  subLabel={<KbaCheckboxSublabel />}
                  checkbox={
                    <Checkbox
                      aria-invalid={Boolean(error)}
                      value="KBA"
                      data-automation-id="kba-proof-checkbox"
                      {...register(name, {
                        required: intl.formatMessage(MESSAGES.PROOF_REQUIREMENT_ERROR),
                      })}
                    />
                  }
                />
              </CheckboxGroup>
            </FormRow>
          </>
        )}
      </>
    </FormCard>
  );
}

type SignerField = { maxSigners?: string; minSigners?: string };
export function MultiSignerSelection<FormFields extends SignerField>({
  form: untypedFormReturn,
  signerFields,
}: {
  form: UseFormReturn<FormFields>;
  signerFields?: { canShowWarning: boolean; count: number };
}) {
  // https://github.com/react-hook-form/react-hook-form/issues/6726
  const form = untypedFormReturn as unknown as UseFormReturn<SignerField>;
  const { register, setValue } = form;
  const minSigners = form.watch("minSigners");
  const maxSigners = form.watch("maxSigners");
  const minSelectId = useId();
  const maxSelectId = useId();

  useEffect(() => {
    if (minSigners! > maxSigners!) {
      setValue("maxSigners", minSigners);
    }
  }, [minSigners]);

  function getSelectItems(minValue: string = "1") {
    const intMinValue = parseInt(minValue, 10);
    const items = [];
    // 10 is max signer limit
    for (let i = intMinValue; i <= 10; i++) {
      items.push({ label: i.toString(), value: i.toString() });
    }
    return items;
  }

  const showDesignationWarning =
    signerFields?.canShowWarning && parseInt(maxSigners!, 10) > signerFields.count;

  return (
    <FormCard
      cardTitle={
        <FormattedMessage
          id="5dbf7352-ac44-4f45-a7a0-42ab88ccf6ce"
          defaultMessage="How many signers?"
        />
      }
      cardDescription={
        signerFields && (
          <FormattedMessage
            id="bd3d224b-e325-47e4-b8c1-340c8144078a"
            defaultMessage="The selected template contains fields for {count} {count, plural, one{signer} other{signers}}."
            values={{ count: signerFields.count }}
          />
        )
      }
      cardDescriptionTextStyle={signerFields && "subtle"}
    >
      <div className={Styles.selectContent}>
        <div className={Styles.selectGroup}>
          <Label className={Styles.selectLabel} htmlFor={minSelectId}>
            <FormattedMessage id="25ee57ac-cdc5-4937-9309-e7db869786e8" defaultMessage="Minimum" />
          </Label>
          <Select
            id={minSelectId}
            items={getSelectItems()}
            aria-invalid="false"
            {...register("minSigners")}
          />
        </div>
        <div className={Styles.selectGroup}>
          <Label className={Styles.selectLabel} htmlFor={maxSelectId}>
            <FormattedMessage id="79fc1294-9dae-4773-812a-d379ebbf8a6f" defaultMessage="Maximum" />
          </Label>
          <Select
            id={maxSelectId}
            items={getSelectItems(minSigners)}
            aria-invalid="false"
            // TODO: 4736 error handling for min > 10
            disabled={minSigners === "10"}
            {...register("maxSigners")}
          />
        </div>
      </div>
      {showDesignationWarning && (
        <AlertMessage kind="warning" className={Styles.designationWarning}>
          <FormattedMessage
            id="05fa3f96-df6e-49f4-ad81-97c845bd0016"
            defaultMessage="The minimum and/or maximum number of signers is greater than the amount of signer fields available on the template."
          />
        </AlertMessage>
      )}
    </FormCard>
  );
}

export function formatProofRequirement(
  values?: ProofRequirementFormType[],
): ProofRequirementInputType {
  return {
    ca: values?.includes("IAL2") ? { selfie: true } : null,
    mfa:
      values?.includes("SMS") || values?.includes("IAL2")
        ? { type: ProofRequirementMfa.SMS }
        : null,
    kba: Boolean(values?.includes("KBA")),
  };
}

export function readProofRequirement(
  values: ProofRequirementInputType | null,
): ProofRequirementFormType[] | undefined {
  const proofValues: ProofRequirementFormType[] = [];
  if (values?.ca?.selfie) {
    proofValues.push("IAL2");
  }
  if (values?.mfa?.type === ProofRequirementMfa.SMS) {
    proofValues.push("SMS");
  }
  if (values?.kba) {
    proofValues.push("KBA");
  }
  return proofValues.length === 0 ? undefined : proofValues;
}
